import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { homeworkAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchTeacherDashboard = createAsyncThunk(
  'homework/fetchTeacherDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getTeacherDashboard();
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch dashboard'));
    }
  }
);

export const fetchHomeworkList = createAsyncThunk(
  'homework/fetchHomeworkList',
  async (params, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.listHomework(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch homework'));
    }
  }
);

export const createHomework = createAsyncThunk(
  'homework/createHomework',
  async (data, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.createHomework(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to create homework'));
    }
  }
);

export const fetchHomework = createAsyncThunk(
  'homework/fetchHomework',
  async (id, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getHomework(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch homework'));
    }
  }
);

export const updateHomework = createAsyncThunk(
  'homework/updateHomework',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.updateHomework(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to update homework'));
    }
  }
);

export const deleteHomework = createAsyncThunk(
  'homework/deleteHomework',
  async (id, { rejectWithValue }) => {
    try {
      await homeworkAPI.deleteHomework(id);
      return id;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to delete homework'));
    }
  }
);

export const fetchAssignments = createAsyncThunk(
  'homework/fetchAssignments',
  async ({ homeworkId, params }, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getAssignments(homeworkId, params);
      return { homeworkId, assignments: res.data };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch assignments'));
    }
  }
);

export const checkHomework = createAsyncThunk(
  'homework/checkHomework',
  async ({ homeworkId, data }, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.checkHomework(homeworkId, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to check homework'));
    }
  }
);

export const checkHomeworkBulk = createAsyncThunk(
  'homework/checkHomeworkBulk',
  async ({ homeworkId, data }, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.checkHomeworkBulk(homeworkId, data);
      return { homeworkId, result: res.data };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to check homework bulk'));
    }
  }
);

export const fetchStudentHomework = createAsyncThunk(
  'homework/fetchStudentHomework',
  async (params, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getStudentHomework(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch homework'));
    }
  }
);

export const fetchStudentHomeworkDetail = createAsyncThunk(
  'homework/fetchStudentHomeworkDetail',
  async (homeworkId, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getStudentHomeworkDetail(homeworkId);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch homework detail'));
    }
  }
);

export const fetchStudentHistory = createAsyncThunk(
  'homework/fetchStudentHistory',
  async (_, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getStudentHistory();
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch history'));
    }
  }
);

export const fetchPerformance = createAsyncThunk(
  'homework/fetchPerformance',
  async (params, { rejectWithValue }) => {
    try {
      const res = await homeworkAPI.getPerformance(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to fetch performance'));
    }
  }
);

const homeworkSlice = createSlice({
  name: 'homework',
  initialState: {
    dashboard: null,
    homeworkList: [],
    currentHomework: null,
    assignments: [],
    studentHomeworkList: [],
    studentHomeworkDetail: null,
    studentHistory: [],
    performance: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearHomeworkError: (state) => {
      state.error = null;
    },
    clearCurrentHomework: (state) => {
      state.currentHomework = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeacherDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchTeacherDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHomeworkList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeworkList.fulfilled, (state, action) => {
        state.loading = false;
        state.homeworkList = action.payload;
      })
      .addCase(fetchHomeworkList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createHomework.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHomework.fulfilled, (state, action) => {
        state.loading = false;
        const items = Array.isArray(action.payload) ? action.payload : [action.payload];
        items.forEach((hw) => state.homeworkList.unshift(hw));
      })
      .addCase(createHomework.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHomework.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomework.fulfilled, (state, action) => {
        state.loading = false;
        state.currentHomework = action.payload;
      })
      .addCase(fetchHomework.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateHomework.fulfilled, (state, action) => {
        state.currentHomework = action.payload;
        const idx = state.homeworkList.findIndex((h) => h.id === action.payload.id);
        if (idx >= 0) state.homeworkList[idx] = action.payload;
      })
      .addCase(deleteHomework.fulfilled, (state, action) => {
        state.homeworkList = state.homeworkList.filter((h) => h.id !== action.payload);
      })
      .addCase(fetchAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload.assignments;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkHomework.fulfilled, (state, action) => {
        const idx = state.assignments.findIndex(
          (a) => a.student_id === action.payload.student_id
        );
        if (idx >= 0) state.assignments[idx] = action.payload;
      })
      .addCase(checkHomeworkBulk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchStudentHomework.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentHomework.fulfilled, (state, action) => {
        state.loading = false;
        state.studentHomeworkList = action.payload.homework_list || [];
        state.studentDashboard = {
          total_homework: action.payload.total_homework,
          completed: action.payload.completed,
          not_completed: action.payload.not_completed,
          pending: action.payload.pending,
        };
      })
      .addCase(fetchStudentHomework.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStudentHomeworkDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentHomeworkDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.studentHomeworkDetail = action.payload;
      })
      .addCase(fetchStudentHomeworkDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStudentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.studentHistory = action.payload;
      })
      .addCase(fetchStudentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.performance = action.payload;
      })
      .addCase(fetchPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearHomeworkError, clearCurrentHomework } = homeworkSlice.actions;
export default homeworkSlice.reducer;
